import { router } from '@inertiajs/react'
import { ArrowLeftIcon } from 'lucide-react'
import { cn, getCsrfToken } from '@/shared/lib/utils'
import { Checkbox } from '@/shared/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { t } from '@/shared/lib/i18n'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { LifecycleStatus } from '@/entities/lot/model/lifecycleStatus'
import type { WebInstrumentsLotForm, WebInstrumentsLotIndex } from '@/shared/types/web'
import { instrumentTaskInstrumentLotLogPath, retireInstrumentLotPath } from '@/shared/lib/routes'
import {
  DEFAULT_LOT_DETAIL_VISIBILITY,
  LotDetailVisibilityCheckboxState,
  lotDetailPassesVisibilityFilters,
} from '@/features/instrument-lots/lotDetailVisibilityFilters'
import {
  LIFECYCLE_BADGE,
  isEditBlockedLifecycleStatus,
  isUnusableLifecycleStatus,
} from '@/entities/lot/model/lifecycleStatus'
import { LotNotesBanner } from '@/features/instrument-lots/LotNotesBanner'
import { RetireLotDetailDialog } from '@/features/instrument-lots/RetireLotDetailDialog'
import { LotForm } from '@/features/instrument-lots/LotForm'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'

const DEFAULT_INSTRUMENT_SHOW_LOT_INERTIA_RELOAD_ONLY = [
  'instrument',
  'lots',
  'currentSchedule',
] as const

type ControlsReagentsLotsModalProps = {
  instrumentNumericId: number
  instrumentTaskNumericId: number
  instrumentTaskLabel: string
  /** The lot whose details are listed and managed in this modal. */
  lotId: number
  lotsGroupedByLot: WebInstrumentsLotIndex[]
  defaultSelectedLotDetailId: number | null
  allowsLotRetirementFromInstrumentPolicy: boolean
  /** Override the trigger button label (defaults to the standard manage button text). */
  triggerLabel?: string
  /**
   * Inertia `only` keys after selection / create / retire. Defaults to Instruments Show props.
   * Dashboard passes flash/errors (and optional task-queue keys) instead.
   */
  inertiaReloadOnly?: readonly string[]
  /**
   * When the parent owns lot data outside Inertia props (Dashboard log_context fetch), call
   * this after a successful mutation so the card can refetch without relying on Show props.
   */
  onLotsMutation?: () => void
}

type LotFormStep =
  | { kind: 'lots-list' }
  | {
      kind: 'lot-form'
      lotId: number
      cloneLotDetailId?: number
      editLotDetailId?: number
      loadingFormData: boolean
      formData?: WebInstrumentsLotForm
      loadError?: string
    }

const LotDetailExpirationTableCellBody = ({
  expirationDateDisplayed,
}: {
  expirationDateDisplayed: string | null
}) => {
  if (!expirationDateDisplayed) return '—'
  return formatCalendarDate(expirationDateDisplayed)
}

type ControlsReagentsLotModalDetailIteration = WebInstrumentsLotIndex['lotDetails'][number]

const deriveControlsReagentsModalRowPiecesDerivedFromLotDetailIteration = ({
  lotDetailRenderedRow,
  defaultSelectedLotDetailId,
  selectionOfDefaultLotDetailFormProcessing,
  allowsLotRetirementFromInstrumentPolicy,
}: {
  lotDetailRenderedRow: ControlsReagentsLotModalDetailIteration
  defaultSelectedLotDetailId: number | null
  selectionOfDefaultLotDetailFormProcessing: boolean
  allowsLotRetirementFromInstrumentPolicy: boolean
}) => {
  const statusLifecycleCode: LifecycleStatus = lotDetailRenderedRow.lifecycleStatus
  const expirationDateDisplayToken =
    lotDetailRenderedRow.openedExpiration ?? lotDetailRenderedRow.expirationDate
  const isCurrentlySelectedDefaultLotDetailRow =
    defaultSelectedLotDetailId === lotDetailRenderedRow.id
  const isLotSelectionDisabledBecauseUnusableLotDetailDerived =
    isUnusableLifecycleStatus(statusLifecycleCode) || selectionOfDefaultLotDetailFormProcessing
  // Edit stays available for discarded/expired lots; only a retired ("Retire Supply") lot blocks
  // it (REBUILD-411). A selection POST in flight still disables it, matching Select.
  const isLotEditDisabledDerived =
    isEditBlockedLifecycleStatus(statusLifecycleCode) || selectionOfDefaultLotDetailFormProcessing
  const isLotRetirementButtonDisabledDerived =
    allowsLotRetirementFromInstrumentPolicy === false ||
    isUnusableLifecycleStatus(statusLifecycleCode) ||
    selectionOfDefaultLotDetailFormProcessing

  return {
    statusLifecycleCode,
    expirationDateDisplayToken,
    isCurrentlySelectedDefaultLotDetailRow,
    isLotSelectionDisabledBecauseUnusableLotDetailDerived,
    isLotEditDisabledDerived,
    isLotRetirementButtonDisabledDerived,
  }
}

export function LotDetailModalRowActionsRibbon({
  postInstrumentLotSelectionForControlsReagentsTaskCallback,
  onOpenLotFormStep,
  onEditLotDetailStep,
  allowsLotRetirementFromInstrumentPolicy,
  setScheduledRetirementLotDetailId,
  processedRowPiecesBundle,
  canEditLot,
  canCloneLot,
}: {
  postInstrumentLotSelectionForControlsReagentsTaskCallback: (lotDetailNumericId: number) => void
  onOpenLotFormStep: (cloneLotDetailId: number) => void
  onEditLotDetailStep: (editLotDetailId: number) => void
  allowsLotRetirementFromInstrumentPolicy: boolean
  setScheduledRetirementLotDetailId: (nextLotDetailId: number) => void
  processedRowPiecesBundle: {
    lotDetailRenderedRow: ControlsReagentsLotModalDetailIteration
    isCurrentlySelectedDefaultLotDetailRow: boolean
    isLotSelectionDisabledBecauseUnusableLotDetailDerived: boolean
    isLotEditDisabledDerived: boolean
    isLotRetirementButtonDisabledDerived: boolean
  }
  // REBUILD-498: the "User" role cannot persist reagent/supply changes, so the mutating
  // row actions (Edit opens an unsavable form; Clone creates a new lot detail) are hidden
  // for that role. Selection stays available — it is the user's permitted action.
  canEditLot: boolean
  canCloneLot: boolean
}) {
  const {
    lotDetailRenderedRow,
    isCurrentlySelectedDefaultLotDetailRow,
    isLotSelectionDisabledBecauseUnusableLotDetailDerived,
    isLotEditDisabledDerived,
    isLotRetirementButtonDisabledDerived,
  } = processedRowPiecesBundle
  return (
    <div className="flex flex-row flex-wrap items-center gap-2 sm:flex-nowrap sm:justify-end">
      {/* The current selection stays clickable so the in-use supply can be re-selected
          (e.g. to re-assert it after editing); it just reads "Selected" and turns primary.
          Disabled only for an unusable lot detail or while a selection POST is in flight. */}
      <Button
        type="button"
        size="sm"
        variant={isCurrentlySelectedDefaultLotDetailRow ? 'default' : 'secondary'}
        className="min-h-9 shrink-0 whitespace-nowrap"
        disabled={isLotSelectionDisabledBecauseUnusableLotDetailDerived}
        aria-pressed={isCurrentlySelectedDefaultLotDetailRow}
        onClick={() =>
          postInstrumentLotSelectionForControlsReagentsTaskCallback(lotDetailRenderedRow.id)
        }
      >
        {isCurrentlySelectedDefaultLotDetailRow
          ? t('instruments.controlsReagents.selectedButton')
          : t('instruments.controlsReagents.selectButton')}
      </Button>
      {canEditLot && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-9 shrink-0 whitespace-nowrap"
          disabled={isLotEditDisabledDerived}
          onClick={() => onEditLotDetailStep(lotDetailRenderedRow.id)}
        >
          {t('instruments.controlsReagents.editLotButton')}
        </Button>
      )}
      {canCloneLot && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-9 shrink-0 whitespace-nowrap"
          onClick={() => onOpenLotFormStep(lotDetailRenderedRow.id)}
        >
          {t('instruments.controlsReagents.cloneLotButton')}
        </Button>
      )}
      {allowsLotRetirementFromInstrumentPolicy ? (
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="min-h-9 shrink-0 whitespace-nowrap"
          disabled={isLotRetirementButtonDisabledDerived}
          onClick={() => setScheduledRetirementLotDetailId(lotDetailRenderedRow.id)}
        >
          {t('instruments.controlsReagents.retireLotButton')}
        </Button>
      ) : null}
    </div>
  )
}

export const ControlsReagentsLotsModal = ({
  instrumentNumericId,
  instrumentTaskNumericId,
  instrumentTaskLabel,
  lotId,
  lotsGroupedByLot,
  defaultSelectedLotDetailId,
  allowsLotRetirementFromInstrumentPolicy,
  triggerLabel,
  inertiaReloadOnly,
  onLotsMutation,
}: ControlsReagentsLotsModalProps) => {
  const [controlsDialogOpenSignal, setControlsDialogOpenSignal] = useState(false)
  const [visibilityCheckboxState, setVisibilityCheckboxState] =
    useState<LotDetailVisibilityCheckboxState>(DEFAULT_LOT_DETAIL_VISIBILITY)
  const [scheduledRetirementLotDetailId, setScheduledRetirementLotDetailId] = useState<
    number | null
  >(null)
  const [step, setStep] = useState<LotFormStep>({ kind: 'lots-list' })

  // Optimistic local selection — immediately reflects a click before the reload returns.
  // Falls back to the prop value (from the server) on open if no local selection has been made.
  const [localSelectedLotDetailId, setLocalSelectedLotDetailId] = useState<number | null>(null)
  const effectiveSelectedLotDetailId = localSelectedLotDetailId ?? defaultSelectedLotDetailId

  const canAddLotDetail = useCan(Resources.LOT_MANAGEMENT, Actions.CREATE)
  // REBUILD-498: Edit mutates an existing lot detail (update); Clone creates a new one (create).
  // The "User" role has neither, so these row affordances are hidden rather than opening an
  // unsavable form.
  const canEditLotDetail = useCan(Resources.LOT_MANAGEMENT, Actions.UPDATE)

  const resolvedInertiaReloadOnly =
    inertiaReloadOnly ?? DEFAULT_INSTRUMENT_SHOW_LOT_INERTIA_RELOAD_ONLY

  const reloadInstrumentLotsAndAssignedTasksFromServerCallback = () => {
    if (onLotsMutation) {
      onLotsMutation()
      return
    }
    router.reload({ only: [...resolvedInertiaReloadOnly] })
  }

  const bucketForLotLinkedWithInstrumentTask =
    lotsGroupedByLot.find((singleLotGrouping) => singleLotGrouping.id === lotId) ?? null

  const [instrumentLotAssignmentRequestInFlight, setInstrumentLotAssignmentRequestInFlight] =
    useState(false)

  const lotFormFetchControllerRef = useRef<AbortController | null>(null)

  const lotDetailVisibilityFilteredRows = useMemo(
    () =>
      bucketForLotLinkedWithInstrumentTask?.lotDetails.filter((lotDetailIterationRow) =>
        lotDetailPassesVisibilityFilters(
          {
            id: lotDetailIterationRow.id,
            lifecycleStatus: lotDetailIterationRow.lifecycleStatus,
          },
          visibilityCheckboxState,
          effectiveSelectedLotDetailId != null ? [effectiveSelectedLotDetailId] : [],
        ),
      ) ?? [],
    [bucketForLotLinkedWithInstrumentTask, visibilityCheckboxState, effectiveSelectedLotDetailId],
  )

  const closeControlsDialogAndResetSteps = useCallback(() => {
    setControlsDialogOpenSignal(false)
    setStep({ kind: 'lots-list' })
    setLocalSelectedLotDetailId(null)
  }, [])

  const postInstrumentLotSelectionForControlsReagentsTaskCallback = useCallback(
    (targetedLotDetailDatabaseId: number) => {
      setLocalSelectedLotDetailId(targetedLotDetailDatabaseId)
      setInstrumentLotAssignmentRequestInFlight(true)
      router.post(
        instrumentTaskInstrumentLotLogPath(instrumentNumericId, instrumentTaskNumericId),
        { instrument_lot_log: { lot_detail_id: targetedLotDetailDatabaseId } },
        {
          preserveScroll: true,
          preserveState: true,
          // `flash` carries the controller's "selection saved" notice, which AppLayout turns
          // into the toast legacy showed. Without it in the partial reload the confirmation
          // never reaches the client (REBUILD-366). Dashboard omits instrument Show keys and
          // refreshes via onLotsMutation instead (REBUILD-438).
          only: [...resolvedInertiaReloadOnly, 'errors', 'flash'],
          // Legacy closed the modal once the save succeeded (closeLotModal). Inertia skips
          // onSuccess when the response carries validation errors, so a rejected selection
          // keeps the modal open with its message instead of closing silently.
          onSuccess: () => {
            closeControlsDialogAndResetSteps()
            onLotsMutation?.()
          },
          onFinish: () => setInstrumentLotAssignmentRequestInFlight(false),
        },
      )
    },
    [
      instrumentNumericId,
      instrumentTaskNumericId,
      closeControlsDialogAndResetSteps,
      resolvedInertiaReloadOnly,
      onLotsMutation,
    ],
  )

  const openLotFormStep = useCallback(
    (cloneLotDetailId?: number) => {
      lotFormFetchControllerRef.current?.abort()
      const controller = new AbortController()
      lotFormFetchControllerRef.current = controller

      setStep({
        kind: 'lot-form',
        lotId: lotId,
        cloneLotDetailId,
        loadingFormData: true,
      })

      const params = new URLSearchParams({ lot_id: String(lotId) })
      if (cloneLotDetailId != null) params.set('clone_lot_detail_id', String(cloneLotDetailId))
      const url = `/instruments/${instrumentNumericId}/lots/form_data?${params.toString()}`

      fetch(url, {
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.json() as Promise<{ form_data: WebInstrumentsLotForm }>
        })
        .then((data) =>
          setStep((current) =>
            current.kind === 'lot-form'
              ? { ...current, loadingFormData: false, formData: data.form_data }
              : current,
          ),
        )
        .catch((error) => {
          if (error instanceof DOMException && error.name === 'AbortError') return
          setStep((current) =>
            current.kind === 'lot-form'
              ? {
                  ...current,
                  loadingFormData: false,
                  loadError: t('instruments.lotsForm.dialog.loadError'),
                }
              : current,
          )
        })
    },
    [instrumentNumericId, lotId],
  )

  const openEditLotDetailStep = useCallback(
    (editLotDetailId: number) => {
      lotFormFetchControllerRef.current?.abort()
      const controller = new AbortController()
      lotFormFetchControllerRef.current = controller

      setStep({
        kind: 'lot-form',
        lotId: lotId,
        editLotDetailId,
        loadingFormData: true,
      })

      const url = `/instruments/${instrumentNumericId}/lots/${editLotDetailId}/form_data`

      fetch(url, {
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.json() as Promise<{ form_data: WebInstrumentsLotForm }>
        })
        .then((data) =>
          setStep((current) =>
            current.kind === 'lot-form'
              ? { ...current, loadingFormData: false, formData: data.form_data }
              : current,
          ),
        )
        .catch((error) => {
          if (error instanceof DOMException && error.name === 'AbortError') return
          setStep((current) =>
            current.kind === 'lot-form'
              ? {
                  ...current,
                  loadingFormData: false,
                  loadError: t('instruments.lotsForm.dialog.loadError'),
                }
              : current,
          )
        })
    },
    [instrumentNumericId, lotId],
  )

  const handleLotFormSuccess = (newLotDetailId?: number) => {
    setStep({ kind: 'lots-list' })
    if (newLotDetailId != null) {
      postInstrumentLotSelectionForControlsReagentsTaskCallback(newLotDetailId)
    } else {
      reloadInstrumentLotsAndAssignedTasksFromServerCallback()
    }
  }

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setControlsDialogOpenSignal(true)
      return
    }
    closeControlsDialogAndResetSteps()
  }

  const isInLotFormStep = step.kind === 'lot-form'

  const lotsListContent = useMemo(
    () => (
      <div className="min-h-0 flex-1 overflow-y-auto pr-1 space-y-4">
        <LotNotesBanner notes={bucketForLotLinkedWithInstrumentTask?.notes} />
        <div className="flex flex-wrap items-center gap-4 border-b pb-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="controls-reagents-hide-expired-lots"
              checked={visibilityCheckboxState.hideExpiredLots}
              onCheckedChange={(nextCheckedPrimitive) =>
                setVisibilityCheckboxState((previousLotVisibilityDraft) => ({
                  ...previousLotVisibilityDraft,
                  hideExpiredLots: nextCheckedPrimitive === true,
                }))
              }
            />
            <label htmlFor="controls-reagents-hide-expired-lots" className="text-sm">
              {t('instruments.controlsReagents.hideExpiredLots')}
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="controls-reagents-hide-retired-lots"
              checked={visibilityCheckboxState.hideRetiredLots}
              onCheckedChange={(nextCheckedPrimitive) =>
                setVisibilityCheckboxState((previousLotVisibilityDraft) => ({
                  ...previousLotVisibilityDraft,
                  hideRetiredLots: nextCheckedPrimitive === true,
                }))
              }
            />
            <label htmlFor="controls-reagents-hide-retired-lots" className="text-sm">
              {t('instruments.controlsReagents.hideRetiredLots')}
            </label>
          </div>
          {canAddLotDetail && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto shrink-0"
              onClick={() => openLotFormStep(undefined)}
            >
              {t('instruments.controlsReagents.addLotDetailButton')}
            </Button>
          )}
        </div>
        <div>
          <div className="space-y-2 sm:hidden">
            <h3 className="text-sm font-medium">
              {t('instruments.controlsReagents.availableLotsHeading')}
            </h3>
            {lotDetailVisibilityFilteredRows.length === 0 ? (
              <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                {t('instruments.controlsReagents.emptyFilteredLots')}
              </p>
            ) : (
              <div className="space-y-3">
                {lotDetailVisibilityFilteredRows.map((lotDetailRenderedRow) => {
                  const derivedRowPiecesFromLotDetailIteration =
                    deriveControlsReagentsModalRowPiecesDerivedFromLotDetailIteration({
                      lotDetailRenderedRow,
                      defaultSelectedLotDetailId: effectiveSelectedLotDetailId,
                      selectionOfDefaultLotDetailFormProcessing:
                        instrumentLotAssignmentRequestInFlight,
                      allowsLotRetirementFromInstrumentPolicy,
                    })

                  return (
                    <div
                      key={lotDetailRenderedRow.id}
                      data-selected={
                        derivedRowPiecesFromLotDetailIteration.isCurrentlySelectedDefaultLotDetailRow
                      }
                      className={cn(
                        'rounded-lg border p-3 shadow-sm ring-1',
                        derivedRowPiecesFromLotDetailIteration.isCurrentlySelectedDefaultLotDetailRow
                          ? 'bg-primary/5 ring-primary/40'
                          : 'bg-muted/25 ring-border/80',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="font-medium leading-snug [overflow-wrap:anywhere]">
                          {lotDetailRenderedRow.lotNumber ?? '—'}
                        </span>
                        <Badge
                          variant={
                            LIFECYCLE_BADGE[
                              derivedRowPiecesFromLotDetailIteration.statusLifecycleCode
                            ] ?? 'outline'
                          }
                          className="shrink-0"
                        >
                          {t(
                            `instruments.lotsIndex.lifecycleStatus.${derivedRowPiecesFromLotDetailIteration.statusLifecycleCode}`,
                          )}
                        </Badge>
                      </div>
                      <div className="mt-3 space-y-1.5 border-t pt-3 text-xs text-muted-foreground">
                        <div className="flex justify-between gap-2">
                          <span>{t('instruments.lotsIndex.table.expiration')}</span>
                          <span className="text-right font-medium text-foreground">
                            <LotDetailExpirationTableCellBody
                              expirationDateDisplayed={
                                derivedRowPiecesFromLotDetailIteration.expirationDateDisplayToken
                              }
                            />
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 border-t pt-3">
                        <LotDetailModalRowActionsRibbon
                          postInstrumentLotSelectionForControlsReagentsTaskCallback={
                            postInstrumentLotSelectionForControlsReagentsTaskCallback
                          }
                          onOpenLotFormStep={openLotFormStep}
                          onEditLotDetailStep={openEditLotDetailStep}
                          canEditLot={canEditLotDetail}
                          canCloneLot={canAddLotDetail}
                          allowsLotRetirementFromInstrumentPolicy={
                            allowsLotRetirementFromInstrumentPolicy
                          }
                          setScheduledRetirementLotDetailId={setScheduledRetirementLotDetailId}
                          processedRowPiecesBundle={{
                            lotDetailRenderedRow,
                            isCurrentlySelectedDefaultLotDetailRow:
                              derivedRowPiecesFromLotDetailIteration.isCurrentlySelectedDefaultLotDetailRow,
                            isLotSelectionDisabledBecauseUnusableLotDetailDerived:
                              derivedRowPiecesFromLotDetailIteration.isLotSelectionDisabledBecauseUnusableLotDetailDerived,
                            isLotEditDisabledDerived:
                              derivedRowPiecesFromLotDetailIteration.isLotEditDisabledDerived,
                            isLotRetirementButtonDisabledDerived:
                              derivedRowPiecesFromLotDetailIteration.isLotRetirementButtonDisabledDerived,
                          }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="hidden space-y-2 sm:block">
            <h3 className="text-sm font-medium">
              {t('instruments.controlsReagents.availableLotsHeading')}
            </h3>
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[32%] [overflow-wrap:anywhere] lg:w-[38%]">
                    {t('instruments.lotsIndex.table.lotNumber')}
                  </TableHead>
                  <TableHead className="w-[12%] whitespace-nowrap lg:w-[10%]">
                    {t('instruments.lotsIndex.table.status')}
                  </TableHead>
                  <TableHead className="w-[14%] whitespace-nowrap lg:w-[14%]">
                    {t('instruments.lotsIndex.table.expiration')}
                  </TableHead>
                  <TableHead className="w-[42%] text-right lg:w-[38%]">
                    {t('instruments.controlsReagents.actionsHeading')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lotDetailVisibilityFilteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-muted-foreground">
                      {t('instruments.controlsReagents.emptyFilteredLots')}
                    </TableCell>
                  </TableRow>
                ) : (
                  lotDetailVisibilityFilteredRows.map((lotDetailRenderedRow) => {
                    const derivedRowPiecesFromLotDetailIteration =
                      deriveControlsReagentsModalRowPiecesDerivedFromLotDetailIteration({
                        lotDetailRenderedRow,
                        defaultSelectedLotDetailId: effectiveSelectedLotDetailId,
                        selectionOfDefaultLotDetailFormProcessing:
                          instrumentLotAssignmentRequestInFlight,
                        allowsLotRetirementFromInstrumentPolicy,
                      })

                    return (
                      <TableRow
                        key={lotDetailRenderedRow.id}
                        data-selected={
                          derivedRowPiecesFromLotDetailIteration.isCurrentlySelectedDefaultLotDetailRow
                        }
                        className={
                          derivedRowPiecesFromLotDetailIteration.isCurrentlySelectedDefaultLotDetailRow
                            ? 'bg-primary/5'
                            : undefined
                        }
                      >
                        <TableCell className="break-words align-middle">
                          {lotDetailRenderedRow.lotNumber ?? '—'}
                        </TableCell>
                        <TableCell className="align-middle whitespace-nowrap">
                          <Badge
                            variant={
                              LIFECYCLE_BADGE[
                                derivedRowPiecesFromLotDetailIteration.statusLifecycleCode
                              ] ?? 'outline'
                            }
                          >
                            {t(
                              `instruments.lotsIndex.lifecycleStatus.${derivedRowPiecesFromLotDetailIteration.statusLifecycleCode}`,
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-middle whitespace-nowrap">
                          <LotDetailExpirationTableCellBody
                            expirationDateDisplayed={
                              derivedRowPiecesFromLotDetailIteration.expirationDateDisplayToken
                            }
                          />
                        </TableCell>
                        <TableCell className="align-middle py-3 text-right">
                          <LotDetailModalRowActionsRibbon
                            postInstrumentLotSelectionForControlsReagentsTaskCallback={
                              postInstrumentLotSelectionForControlsReagentsTaskCallback
                            }
                            onOpenLotFormStep={openLotFormStep}
                            onEditLotDetailStep={openEditLotDetailStep}
                            canEditLot={canEditLotDetail}
                            canCloneLot={canAddLotDetail}
                            allowsLotRetirementFromInstrumentPolicy={
                              allowsLotRetirementFromInstrumentPolicy
                            }
                            setScheduledRetirementLotDetailId={setScheduledRetirementLotDetailId}
                            processedRowPiecesBundle={{
                              lotDetailRenderedRow,
                              isCurrentlySelectedDefaultLotDetailRow:
                                derivedRowPiecesFromLotDetailIteration.isCurrentlySelectedDefaultLotDetailRow,
                              isLotSelectionDisabledBecauseUnusableLotDetailDerived:
                                derivedRowPiecesFromLotDetailIteration.isLotSelectionDisabledBecauseUnusableLotDetailDerived,
                              isLotEditDisabledDerived:
                                derivedRowPiecesFromLotDetailIteration.isLotEditDisabledDerived,
                              isLotRetirementButtonDisabledDerived:
                                derivedRowPiecesFromLotDetailIteration.isLotRetirementButtonDisabledDerived,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    ),
    [
      visibilityCheckboxState,
      lotDetailVisibilityFilteredRows,
      instrumentLotAssignmentRequestInFlight,
      effectiveSelectedLotDetailId,
      allowsLotRetirementFromInstrumentPolicy,
      canAddLotDetail,
      canEditLotDetail,
      openLotFormStep,
      openEditLotDetailStep,
      bucketForLotLinkedWithInstrumentTask,
      postInstrumentLotSelectionForControlsReagentsTaskCallback,
    ],
  )

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={() => setControlsDialogOpenSignal(true)}
      >
        {triggerLabel ?? t('instruments.controlsReagents.manageButton')}
      </Button>
      <Dialog open={controlsDialogOpenSignal} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="flex h-[calc(100dvh-2rem)] max-h-[90vh] w-[calc(100vw-1rem)] max-w-none flex-col gap-4 overflow-hidden sm:h-auto sm:max-h-[92vh] sm:max-w-[min(1420px,calc(100vw-2rem))] sm:p-6">
          <DialogHeader className="shrink-0">
            {isInLotFormStep ? (
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  onClick={() => setStep({ kind: 'lots-list' })}
                >
                  <ArrowLeftIcon className="size-4" />
                  {t('instruments.controlsReagents.backToLotsButton')}
                </Button>
                <DialogTitle>
                  {step.kind === 'lot-form' && step.cloneLotDetailId != null
                    ? t('instruments.controlsReagents.cloneLotDialogTitle')
                    : step.kind === 'lot-form' && step.editLotDetailId != null
                      ? t('instruments.lotsForm.editTitle')
                      : t('instruments.controlsReagents.addLotDetailDialogTitle')}
                </DialogTitle>
              </div>
            ) : (
              <DialogTitle>
                {t('instruments.controlsReagents.dialogTitle')} — {instrumentTaskLabel}
              </DialogTitle>
            )}
          </DialogHeader>

          {isInLotFormStep ? (
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {step.kind === 'lot-form' && step.loadingFormData && (
                <div className="flex items-center justify-center py-12">
                  <p className="text-sm text-muted-foreground">
                    {t('instruments.lotsForm.dialog.loading')}
                  </p>
                </div>
              )}
              {step.kind === 'lot-form' && step.loadError && (
                <div className="rounded border border-destructive bg-destructive/10 px-4 py-3">
                  <p className="text-sm text-destructive">{step.loadError}</p>
                </div>
              )}
              {step.kind === 'lot-form' && !step.loadingFormData && step.formData && (
                <LotForm
                  instrumentId={instrumentNumericId}
                  formData={step.formData}
                  fromModal
                  reloadOnly={[...resolvedInertiaReloadOnly, 'errors']}
                  onSuccess={handleLotFormSuccess}
                  onCancel={() => setStep({ kind: 'lots-list' })}
                />
              )}
            </div>
          ) : bucketForLotLinkedWithInstrumentTask == null ? (
            <p className="text-sm text-muted-foreground">
              {t('instruments.controlsReagents.missingLot')}
            </p>
          ) : (
            lotsListContent
          )}

          <DialogFooter className="shrink-0">
            <Button type="button" variant="secondary" onClick={() => handleDialogOpenChange(false)}>
              {t('instruments.controlsReagents.closeButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {scheduledRetirementLotDetailId != null ? (
        <RetireLotDetailDialog
          isOpen
          closeDialogCallback={() => setScheduledRetirementLotDetailId(null)}
          retireSubmissionPathForLotDetail={retireInstrumentLotPath(
            instrumentNumericId,
            scheduledRetirementLotDetailId,
          )}
          afterSuccessfulSubmissionCallback={reloadInstrumentLotsAndAssignedTasksFromServerCallback}
        />
      ) : null}
    </>
  )
}
